import { normalizeStoragePathWithSlash } from 'domain/shared/documentStorage';
import {
  AssignmentFormItem,
  ComplianceDocumentCommitType,
  ComplianceDocumentFormType,
  ComplianceDocumentMetadata,
  ComplianceDocumentType,
  ComplianceDocumentTypeFE,
  RelatedEmployeeFilter,
  RelatedEmployeeForm,
} from './types';
import { COMPLIANCE_DOCUMENT_STATUS } from './constants';
import { ListResponse } from 'framework/list';
import { getTargetAudienceValues } from './shared/targetAudienceHelper';
import { ProjectSearchBaseDto, AccountDto } from 'domain/shared/newProject';

const mapToRelatedEmployeeFilter = (params: ComplianceDocumentFormType): Nullable<RelatedEmployeeFilter> => {
  const sourceData = {
    isAllEmployee: !!params.isAllEmployee,
    positionIds: params.positionIds || [],
    offices: params.offices || [],
    businessRegistrationCountries: params.businessRegistrationCountries || [],
  };

  if (sourceData.isAllEmployee) {
    return null;
  }

  const filter: RelatedEmployeeFilter = {};

  if (sourceData.positionIds.length > 0) {
    filter.positionId = {
      in: [...sourceData.positionIds],
    };
  }

  if (sourceData.offices.length > 0) {
    filter.officeId = {
      in: [...sourceData.offices],
    };
  }

  if (sourceData.businessRegistrationCountries.length > 0) {
    filter.businessRegistrationCountry = {
      in: [...sourceData.businessRegistrationCountries],
    };
  }

  return filter;
};

export const mapFromRelatedEmployeeFilter = (source?: Nullable<ComplianceDocumentMetadata>): RelatedEmployeeForm => {
  const result: RelatedEmployeeForm = {
    isAllEmployee: true,
    positionIds: [],
    offices: [],
    businessRegistrationCountries: [],
    assignments: [],
  };

  const relatedFilter = source?.relatedEmployeeFilter;
  if (
    !relatedFilter &&
    !source?.relatedEmployeeContractTypes?.length &&
    !source?.relatedEmployeeAccountIds?.length &&
    !source?.relatedEmployeeProjectIds?.length
  ) {
    return result;
  }

  result.isAllEmployee = false;

  if (relatedFilter?.positionId?.in) {
    result.positionIds = [...relatedFilter.positionId.in];
  }

  if (relatedFilter?.officeId?.in) {
    result.offices = [...relatedFilter.officeId.in];
  }

  if (relatedFilter?.businessRegistrationCountry?.in) {
    result.businessRegistrationCountries = [...relatedFilter.businessRegistrationCountry.in];
  }

  const accountAssignments = (source?.relatedEmployeeAccountIds || []).map(
    (id): AssignmentFormItem => ({
      id,
      name: String(id),
      account: true,
    }),
  );
  const projectAssignments = (source?.relatedEmployeeProjectIds || []).map(
    (id): AssignmentFormItem => ({
      id,
      name: String(id),
      account: false,
    }),
  );
  result.assignments = [...accountAssignments, ...projectAssignments];

  return result;
};

export const enrichComplianceDocument = (item: ComplianceDocumentType): ComplianceDocumentTypeFE => {
  return {
    ...item,
    metadata: {
      ...item.metadata,
      complianceName: item.metadata.complianceName || item.metadata.entityId,
      complianceType: item.metadata.complianceType,
      status: item.metadata.status || COMPLIANCE_DOCUMENT_STATUS.UNPUBLISHED,
      ...mapFromRelatedEmployeeFilter(item.metadata),
    },
  };
};

export const buildLatestComplianceDocuments = (
  documents: ComplianceDocumentTypeFE[],
  getMomentDateObj: (value: any) => any,
  getTimeDifference: (a: any, b: any) => number,
): ComplianceDocumentTypeFE[] => {
  const groupedByComplianceDocument: Record<string, ComplianceDocumentTypeFE[]> = documents.reduce(
    (acc: Record<string, ComplianceDocumentTypeFE[]>, doc: ComplianceDocumentTypeFE) => {
      const key = String(doc.metadata.entityId || '');
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(doc);
      return acc;
    },
    {},
  );

  return Object.values(groupedByComplianceDocument)
    .map(docs => {
      const sorted = docs.slice().sort((a, b) => {
        const versionsDiff = (b.metadata.version || 0) - (a.metadata.version || 0);
        if (versionsDiff !== 0) {
          return versionsDiff;
        }

        return getMomentDateObj(b.metadata.createdAt).diff(getMomentDateObj(a.metadata.createdAt));
      });

      const [latest, ...previousVersions] = sorted;

      return {
        ...latest,
        metadata: {
          ...latest.metadata,
          previousVersions,
        },
      };
    })
    .sort((a, b) => {
      const dateA = getMomentDateObj(a.metadata.createdAt);
      const dateB = getMomentDateObj(b.metadata.createdAt);
      return getTimeDifference(dateA, dateB);
    });
};

export const mapComplianceDocumentForCommit = (docData: ComplianceDocumentFormType): ComplianceDocumentCommitType => {
  const paths = docData.paths?.length ? docData.paths : [];
  const relatedEmployeeContractTypes = docData.relatedEmployeeContractTypes || [];
  const assignments = docData.assignments || [];
  const isSignatureRequired = docData.isSignatureRequired ?? true;
  const relatedEmployeeAccountIds = assignments
    .filter(assignment => assignment.account)
    .map(assignment => String(assignment.id));
  const relatedEmployeeProjectIds = assignments
    .filter(assignment => !assignment.account)
    .map(assignment => String(assignment.id));

  return {
    complianceName: docData.complianceName,
    complianceType: docData.complianceType,
    status: docData.status,
    ...(docData.version ? { version: docData.version } : {}),
    relatedEmployeeFilter: mapToRelatedEmployeeFilter(docData),
    ...(relatedEmployeeContractTypes.length ? { relatedEmployeeContractTypes: relatedEmployeeContractTypes } : {}),
    ...(relatedEmployeeAccountIds.length ? { relatedEmployeeAccountIds } : {}),
    ...(relatedEmployeeProjectIds.length ? { relatedEmployeeProjectIds } : {}),
    isSignatureRequired,
    ...(isSignatureRequired
      ? docData.gracePeriodDays !== undefined
        ? { gracePeriodDays: docData.gracePeriodDays }
        : {}
      : { gracePeriodDays: null }),
    paths: paths.length ? [normalizeStoragePathWithSlash(paths[0])] : [],
  };
};

export const mapComplianceDocumentForList = (item: ComplianceDocumentType) => ({
  ...item,
  targetAudienceValues: getTargetAudienceValues({
    relatedEmployeeFilter: item.metadata.relatedEmployeeFilter,
    assignments: item.metadata.relatedEmployeeProjectIds,
    accounts: item.metadata.relatedEmployeeAccountIds,
    contractType: item.metadata.relatedEmployeeContractTypes,
  }),
  metadata: {
    ...item.metadata,
    complianceName: item.metadata.complianceName || item.metadata.entityId,
    complianceType: item.metadata.complianceType,
    status: item.metadata.status || COMPLIANCE_DOCUMENT_STATUS.UNPUBLISHED,
    ...mapFromRelatedEmployeeFilter(item.metadata),
  },
});

export const mapAssignmentsWithProjects = async (
  documents: ComplianceDocumentType[],
  searchBase: (request: Record<string, unknown>) => Promise<ListResponse<ProjectSearchBaseDto>>,
  searchAccounts: (request: Record<string, unknown>) => Promise<ListResponse<AccountDto>>,
): Promise<ComplianceDocumentTypeFE[]> => {
  const projectLegacyIds = Array.from(
    new Set(documents.flatMap(item => item.metadata?.relatedEmployeeProjectIds || []).map(id => String(id))),
  );
  const accountIds = Array.from(
    new Set(documents.flatMap(item => item.metadata?.relatedEmployeeAccountIds || []).map(id => String(id))),
  );

  const projectNameByLegacyId = new Map<string, string>();
  const accountNameById = new Map<string, string>();

  if (projectLegacyIds.length) {
    const projectsResponse = await searchBase({
      pageNumber: 0,
      pageSize: 100,
      filter: {
        legacyId: {
          in: projectLegacyIds,
        },
      },
    });

    (projectsResponse.result || []).forEach(project => {
      projectNameByLegacyId.set(String(project.legacyId), project.name);
    });
  }

  if (accountIds.length) {
    const accountsResponse = await searchAccounts({
      pageNumber: 0,
      pageSize: 100,
      filter: {
        alias: {
          in: accountIds,
        },
      },
    });

    (accountsResponse.result || []).forEach(account => {
      accountNameById.set(account.alias, account.codeName || account.alias);
    });
  }

  return documents.map(item => {
    const document = mapComplianceDocumentForList(item);

    return {
      ...document,
      metadata: {
        ...document.metadata,
        assignments: (document.metadata.assignments || []).map(assignment => {
          if (assignment.account) {
            const accountName = accountNameById.get(String(assignment.id));
            return {
              ...assignment,
              name: accountName || assignment.name,
            };
          }

          const projectName = projectNameByLegacyId.get(String(assignment.id));
          return {
            ...assignment,
            name: projectName || assignment.name,
          };
        }),
      },
    };
  });
};
